import React from 'react';
import { NavLink } from 'react-router-dom';

import founderPhoto from '../assets/psu-profile.png';

import './Aboutus.scss';

/* ── FAQ — unique to About Us ───────────────────────────────── */

const questions = [
  {
    question: 'Who finds and evaluates the international recruits?',
    answer:
      'Kajetan Borecki — founder — along with his scouts recruit athletes on our platform. They search for elite prospects all over Poland/Europe and collaborate with coaches from Polish National Teams as well as Elite European clubs. The aim is to select the most promising athletes who have capabilities to compete in college sports at the highest level.',
  },
  {
    question: 'Does PlaySportUSA place athletes with favored schools?',
    answer:
      'No. PlaySportUSA serves all subscribing coaches equally by publishing complete profiles on "Available Players". Each subscriber gets an equal right to recruit a student-athlete and has access to contact information to reach out directly. We want every coach to be able to present their University and be considered by our athletes.',
  },
  {
    question: 'At what levels are PlaySportUSA athletes?',
    answer:
      'Mid-major DI. Some can thrive in the top-25 NCAA Division I. Most are better suited for D2 NCAA, NJCAA, NAIA teams.',
  },
  {
    question: 'Can I see videos of the recruits?',
    answer:
      "Yes! Logging into the PlaySportUSA platform gives you full access to videos of each recruit, along with all necessary information to evaluate if they're a good fit for your program.",
  },
  {
    question: 'Who pays for the PlaySportUSA service?',
    answer:
      'Subscribing athletes pay for the service. Subscription for American coaches is completely FREE.',
  },
];

/* ── Scouting process steps — unique to About Us ─────────────── */

const scoutingSteps = [
  {
    number: '01',
    icon: 'fa-binoculars',
    title: 'Identify Talent',
    desc: 'Our scouts attend tournaments, work with national team coaches, and review game footage across Europe to find elite prospects.',
  },
  {
    number: '02',
    icon: 'fa-clipboard-check',
    title: 'Evaluate & Vet',
    desc: 'Each athlete is evaluated on athletic performance, academics, character, and NCAA eligibility before being accepted onto the platform.',
  },
  {
    number: '03',
    icon: 'fa-video',
    title: 'Build Profiles',
    desc: 'We create comprehensive profiles with highlight videos, stats, academic records, and evaluations — everything a coach needs.',
  },
  {
    number: '04',
    icon: 'fa-handshake',
    title: 'Connect with Coaches',
    desc: 'Profiles go live for all registered coaches. Coaches can contact athletes directly — no middleman, no bias.',
  },
];

const AboutUs = () => {
  return (
    <div className="about">
      {/* ═══ Hero — Mission focused ═══════════════════════════════ */}
      <section className="about__hero">
        <div className="about__hero-content">
          <h1 className="about__hero-title">
            Bridging the Gap Between<br />
            <span>European Talent & American Colleges</span>
          </h1>
          <p className="about__hero-subtitle">
            PlaySportUSA was founded with a simple mission: give talented international
            athletes a fair chance at competing in American college sports — and give
            coaches a reliable way to find them.
          </p>
        </div>
      </section>

      {/* ═══ Our Story + Founder ═════════════════════════════════ */}
      <section className="about__story">
        <h2 className="about__section-title">Our Story</h2>
        <div className="about__story-content">
          <div className="about__story-text">
            <p>
              PlaySportUSA was created by <strong>Kajetan Borecki</strong>, a former
              international athlete and NCAA D1 ( University of Florida, Youngstown State) NCAA D2 ( Valdosta State University) coach who experienced firsthand how difficult it is for talented European athletes to get noticed by American college coaches.
            </p>
            <p>
              The gap wasn't talent — it was <strong>visibility</strong>. Incredible athletes
              were competing at the highest levels in Europe, but had no direct way to connect
              with NCAA programs looking for exactly their skills.
            </p>
            <p>
              That's why we built PlaySportUSA: a platform where every profile is
              scout-verified, every video is real game footage, and every coach gets
              equal access — completely free.
            </p>
          </div>
          <div className="about__story-highlights">
            <div className="about__highlight">
              <i className="fas fa-globe-americas" />
              <div>
                <strong>European Scouting Network</strong>
                <span>Working with national team coaches and elite clubs</span>
              </div>
            </div>
            <div className="about__highlight">
              <i className="fas fa-shield-alt" />
              <div>
                <strong>Every Athlete Vetted</strong>
                <span>Performance, academics, and character evaluated</span>
              </div>
            </div>
            <div className="about__highlight">
              <i className="fas fa-dollar-sign" />
              <div>
                <strong>Free for Coaches — Always</strong>
                <span>Full access, no subscription, no hidden fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Founder ═══════════════════════════════════════════════ */}
      <section className="about__founder">
        <h2 className="about__section-title">Meet the Founder</h2>
        <div className="about__founder-card">
          <div className="about__founder-photo">
            <img src={founderPhoto} alt="Kajetan Borecki — Founder of PlaySportUSA" />
          </div>
          <div className="about__founder-info">
            <h3 className="about__founder-name">Kajetan Borecki</h3>
            <p className="about__founder-role">Founder &amp; Head Scout</p>
            <p className="about__founder-bio">
              Kajetan brings a unique perspective to international recruiting — he's been on
              both sides of the game. As a player he competed at the NCAA Division I level and
              professionally in Europe. As a coach in the USA he's worked with programs across
              multiple states and divisions in NCAA D1 and NCAA D2. That firsthand experience shapes
              everything we do at PlaySportUSA.
            </p>

            <div className="about__founder-credentials">
              <div className="about__founder-section">
                <h4><i className="fas fa-volleyball-ball" /> As a Player</h4>
                <ul>
                  <li><strong>NCAA Division I</strong> — New Jersey Institute of Technology (NJIT)</li>
                  <li><strong>CAJVB (French Professional League)</strong></li>
                </ul>
              </div>
              <div className="about__founder-section">
                <h4><i className="fas fa-whistle" /> As a Coach</h4>
                <ul>
                  <li><strong>NCAA Division I &amp; Division II</strong></li>
                  <li>Coaching experience in University of Florida, Youngstown State, Valdosta State</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Scouting Process ═════════════════════════════════════ */}
      <section className="about__scouting">
        <h2 className="about__section-title">How We Find Athletes</h2>
        <p className="about__section-subtitle">
          Every athlete on PlaySportUSA goes through a rigorous scouting process
          before their profile goes live.
        </p>
        <div className="about__scouting-steps">
          {scoutingSteps.map((step, i) => (
            <div key={i} className="about__scouting-step">
              <div className="about__scouting-number">{step.number}</div>
              <div className="about__scouting-icon">
                <i className={`fas ${step.icon}`} />
              </div>
              <h3 className="about__scouting-title">{step.title}</h3>
              <p className="about__scouting-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ══════════════════════════════════════════════════ */}
      <section className="about__faq">
        <h2 className="about__section-title">Common Questions</h2>
        <div className="about__questions">
          {questions.map((item, index) => (
            <div key={index} className="about__question">
              <h4 className="about__question-title">
                <i className="fas fa-chevron-right" />
                {item.question}
              </h4>
              <p className="about__question-answer">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CTA ══════════════════════════════════════════════════ */}
      <section className="about__cta">
        <h2>Start Recruiting International Talent</h2>
        <p>Create your free account and browse athlete profiles today.</p>
        <div className="about__cta-actions">
          <NavLink to="/create-account" className="about__btn about__btn--primary about__btn--large">
            <i className="fas fa-arrow-right" /> Create Free Account
          </NavLink>
          <NavLink to="/available-players" className="about__btn about__btn--secondary about__btn--large">
            <i className="fas fa-users" /> Browse Athletes
          </NavLink>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
